import { useEffect } from 'react';

const TEXT_INPUT_TYPES = new Set([
  'text',
  'search',
  'email',
  'tel',
  'url',
  'password',
  'number',
  'date',
  'datetime-local',
  'time',
  'month',
  'week',
]);

function isTextField(el: Element | null): boolean {
  if (!el) return false;
  if (el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) return true;
  if (el instanceof HTMLInputElement) return TEXT_INPUT_TYPES.has(el.type || 'text');
  return el instanceof HTMLElement && el.isContentEditable;
}

/**
 * While a text field has focus on a phone the on-screen keyboard is up. Marks
 * `<html data-keyboard="open">` so the fixed bottom navigation and the
 * assistant button can step aside (CSS in safeArea.css) — only the field being
 * filled stays above the keyboard. Cleared as soon as focus leaves a field.
 */
export function useKeyboardOpen(): void {
  useEffect(() => {
    const root = document.documentElement;
    const phone = window.matchMedia('(max-width: 899px)');
    let timer: number | undefined;
    const update = () => {
      window.clearTimeout(timer);
      // Focus moves between fields within a tick; look after it settles.
      timer = window.setTimeout(() => {
        if (phone.matches && isTextField(document.activeElement)) {
          root.setAttribute('data-keyboard', 'open');
        } else {
          root.removeAttribute('data-keyboard');
        }
      }, 60);
    };
    document.addEventListener('focusin', update);
    document.addEventListener('focusout', update);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('focusin', update);
      document.removeEventListener('focusout', update);
      root.removeAttribute('data-keyboard');
    };
  }, []);
}
